/**
 * Data Access Layer for Customer Billing
 * 
 * Fetches LLM calls by customer ID with period-based filtering
 * Handles pagination for complete data retrieval
 */

import { API, graphqlOperation } from 'aws-amplify';
import { GraphQLResult } from '@aws-amplify/api-graphql';
import { callsByCustomerIdAndCreatedAt } from '../graphql/queries';
import { CallsByCustomerIdAndCreatedAtQuery, CallsByCustomerIdAndCreatedAtQueryVariables, LLMCall, ModelSortDirection } from '../API';
import { PeriodData } from '../utils/periodCalculations';

export interface CustomerCallsResult {
  calls: LLMCall[];
  totalCount: number;
  pagesRetrieved: number;
  fetchTimeMs: number;
  truncated: boolean; // True if hit the 50k safety limit
}

/**
 * Fetch all LLM calls for a customer within a specific period
 * Handles pagination automatically to ensure complete data retrieval
 */
export async function fetchLLMCallsByCustomerIdAndPeriod(
  customerId: string,
  period: PeriodData
): Promise<CustomerCallsResult> {
  const startTime = Date.now();
  const allCalls: LLMCall[] = [];
  let nextToken: string | null = null;
  let hasMorePages = true;
  let pagesRetrieved = 0;
  let truncated = false;
  
  while (hasMorePages) {
    const result = await fetchLLMCallsByCustomerIdRecursive(
      customerId,
      period,
      nextToken
    ) as GraphQLResult<CallsByCustomerIdAndCreatedAtQuery>;
    
    pagesRetrieved++;
    
    if (result.data?.callsByCustomerIdAndCreatedAt?.items) {
      const items = result.data.callsByCustomerIdAndCreatedAt.items.filter(
        (item: any): item is LLMCall => item !== null
      );
      allCalls.push(...items);
    }
    
    nextToken = result.data?.callsByCustomerIdAndCreatedAt?.nextToken || null;
    hasMorePages = !!nextToken;
    
    // Safety break for customers with excessive call volumes
    if (allCalls.length > 50000) {
      console.warn(`Customer ${customerId} has >50k calls, truncating for performance`);
      truncated = true;
      break;
    }
    
    // Additional safety: prevent infinite loops with too many pages
    if (pagesRetrieved > 100) {
      console.warn(`Customer ${customerId} required >100 pages, stopping for performance`);
      truncated = true;
      break;
    }
  }
  
  const fetchTimeMs = Date.now() - startTime;
  
  console.log(`🔍 [Fetch Debug] Final Summary:`, {
    customerId,
    periodType: period.type,
    periodLabel: period.label,
    totalCalls: allCalls.length,
    pagesRetrieved,
    fetchTimeMs,
    truncated,
    dateRange: `${period.startDate.toISOString()} to ${(period.actualEndDate || period.endDate).toISOString()}`
  });
  
  if (allCalls.length > 0) {
    console.log(`🔍 [Fetch Debug] Sample calls:`, allCalls.slice(0, 3).map(call => ({
      id: call.id,
      createdAt: call.createdAt,
      customerId: call.customerId
    })));
  }
  
  console.log(`Fetched ${allCalls.length} calls for customer ${customerId} in ${fetchTimeMs}ms (${pagesRetrieved} pages)`);
  
  return {
    calls: allCalls,
    totalCount: allCalls.length,
    pagesRetrieved,
    fetchTimeMs,
    truncated
  };
}

/**
 * Internal recursive helper function with UTC period filtering
 * Fetches a single page of results with proper GraphQL query structure
 */
async function fetchLLMCallsByCustomerIdRecursive(
  customerId: string,
  period: PeriodData,
  nextToken: string | null = null
): Promise<GraphQLResult<CallsByCustomerIdAndCreatedAtQuery>> {
  
  // Build the query variables according to the GraphQL schema
  const variables: CallsByCustomerIdAndCreatedAtQueryVariables = {
    customerId: customerId,
    sortDirection: ModelSortDirection.DESC, // Most recent first
    limit: 1000, // Use larger page size for fewer round trips
    nextToken: nextToken
  };

  // Add UTC-based period filtering using the createdAt range
  const actualEndDate = period.actualEndDate || period.endDate;
  variables.createdAt = {
    between: [
      period.startDate.toISOString(),
      actualEndDate.toISOString()
    ]
  };

  /*
  console.log(`🔍 [Query Debug] Fetching calls for customer: ${customerId}`);
  console.log(`🔍 [Query Debug] Period: ${period.type} (${period.label})`);
  console.log(`🔍 [Query Debug] Date range: ${period.startDate.toISOString()} to ${actualEndDate.toISOString()}`);
  console.log(`🔍 [Query Debug] GraphQL variables:`, JSON.stringify(variables, null, 2));
*/
  try {
    const result = await API.graphql(
      graphqlOperation(callsByCustomerIdAndCreatedAt, variables)
    ) as GraphQLResult<CallsByCustomerIdAndCreatedAtQuery>;

    console.log(`🔍 [Query Debug] Query result:`, {
      itemsCount: result.data?.callsByCustomerIdAndCreatedAt?.items?.length || 0,
      hasNextToken: !!result.data?.callsByCustomerIdAndCreatedAt?.nextToken,
      firstItem: result.data?.callsByCustomerIdAndCreatedAt?.items?.[0] ? {
        id: result.data.callsByCustomerIdAndCreatedAt.items[0].id,
        createdAt: result.data.callsByCustomerIdAndCreatedAt.items[0].createdAt,
        customerId: result.data.callsByCustomerIdAndCreatedAt.items[0].customerId
      } : null,
      errors: result.errors
    });

    return result;
  } catch (error) {
    console.error('Error fetching customer calls:', error);
    throw new Error(`Failed to fetch calls for customer ${customerId}: ${error}`);
  }
}

/**
 * Fetch a limited preview of customer calls (for quick loading)
 * Returns the most recent 1000 calls without full pagination
 */
export async function fetchLLMCallsPreview(
  customerId: string,
  period: PeriodData
): Promise<CustomerCallsResult> {
  const startTime = Date.now();
  
  try {
    const result = await fetchLLMCallsByCustomerIdRecursive(customerId, period, null);
    
    const calls = result.data?.callsByCustomerIdAndCreatedAt?.items?.filter(
      (item: any): item is LLMCall => item !== null
    ) || [];
    
    const fetchTimeMs = Date.now() - startTime;
    
    return {
      calls,
      totalCount: calls.length,
      pagesRetrieved: 1,
      fetchTimeMs,
      truncated: !!result.data?.callsByCustomerIdAndCreatedAt?.nextToken // True if more data available
    };
  } catch (error) {
    console.error('Error fetching customer calls preview:', error);
    throw error;
  }
}

/**
 * Validate customer ID and period before making requests
 */
export function validateBillingRequest(customerId: string, period: PeriodData): { isValid: boolean; errors: string[] } {
  const errors: string[] = [];
  
  if (!customerId || customerId.trim() === '') {
    errors.push('Customer ID is required');
  }
  
  if (!period.startDate || !period.endDate) {
    errors.push('Period start and end dates are required');
  }
  
  if (period.startDate >= period.endDate) {
    errors.push('Period start date must be before end date');
  }
  
  // Check for reasonable period length (not more than 1 year)
  const maxPeriodMs = 365 * 24 * 60 * 60 * 1000; // 1 year in milliseconds
  if (period.endDate.getTime() - period.startDate.getTime() > maxPeriodMs) {
    errors.push('Period length cannot exceed 1 year');
  }
  
  return {
    isValid: errors.length === 0,
    errors
  };
}

/**
 * Performance monitoring interface for billing operations
 */
export interface BillingPerformanceMetrics {
  totalCallsProcessed: number;
  fetchTimeMs: number;
  calculationTimeMs: number;
  pagesRetrieved: number;
  cacheHit: boolean;
  memoryUsageMB?: number;
}

/**
 * Log performance metrics for monitoring and optimization
 */
export function logBillingPerformance(
  customerId: string,
  period: PeriodData,
  metrics: BillingPerformanceMetrics
): void {
  const logData = {
    customerId,
    periodType: period.type,
    periodDays: period.totalDays,
    ...metrics,
    timestamp: new Date().toISOString()
  };
  
  // Log performance data (in production, this could go to monitoring service)
  if (metrics.totalCallsProcessed > 10000 || metrics.fetchTimeMs > 30000) {
    console.warn('High-volume billing operation:', logData);
  } else {
    console.log('Billing operation completed:', logData);
  }
}
